/**
 * B1067 + B1068 — Vue admin globale des vérifications de synthèses
 * (Eric 2026-09-04 SPEC STRICK PROD).
 *
 * Le panneau par vidéo répond à « qu'est-ce qui a été vérifié ICI ». Cette
 * page répond à « où en est-on globalement » : toutes les vérifications et
 * corrections de l'organisation, plus la liste des synthèses modifiées après
 * leur vérification (B1068) — celles qui affichent « périmée » sans que
 * personne n'ouvre la vidéo pour le voir.
 */
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ArrowClockwise, ClockCounterClockwise, EnvelopeSimple, Lock, WarningCircle } from "@phosphor-icons/react";
import { ReverifyStaleButton } from "../../components/admin/ReverifyStaleButton";
import { GuidedStaleReview } from "../../components/admin/GuidedStaleReview";
import { LockPassageButton } from "../../components/video/SynthesisLocks";
import { useLang } from "../../contexts/LanguageContext";

const API = process.env.REACT_APP_BACKEND_URL;

const fmt = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("fr-CA", { dateStyle: "medium", timeStyle: "short" });
  } catch {
    return String(iso);
  }
};

export default function SynthesisVerificationsPage() {
  const { tr } = useLang();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [removing, setRemoving] = useState(null);
  // B1112 — Mode d'affichage de la liste des synthèses périmées. Par défaut
  // le nom de fichier : c'est l'unité de travail d'Eric quand il révise.
  const [displayBy, setDisplayBy] = useState("filename");
  // B1123 (Eric 2026-09-08) — Filtre par utilisateur sur l'historique : devant
  // une modification surprise, la première question est « qui ? ».
  const [historyAuthor, setHistoryAuthor] = useState("");
  // B1123 v2 — Onglet affiché. « Synthèses périmées » par défaut : c'est la
  // liste sur laquelle il y a quelque chose à faire.
  const [tab, setTab] = useState("perimees");

  // B1125 — Envoi manuel du rappel hebdomadaire (les courriels sont
  // volontairement bloqués en préproduction : la réponse dit alors
  // « aucun destinataire » ou liste les adresses sans rien envoyer).
  const [sendingReminder, setSendingReminder] = useState(false);
  const sendReminder = async () => {
    setSendingReminder(true);
    try {
      const { data: res } = await axios.post(
        `${API}/api/admin/stale-syntheses/send-reminder`, {}, { withCredentials: true });
      if (res.sent) {
        toast.success(`Rappel envoyé à ${(res.recipients || []).join(", ")} (${res.stale_count} périmée(s))`);
      } else {
        toast.info(`Aucun envoi : ${res.reason || "raison inconnue"}`);
      }
    } catch (e) {
      toast.error("Envoi impossible : " + (e?.response?.data?.detail || e.message));
    } finally {
      setSendingReminder(false);
    }
  };

  const load = useCallback(async (by = historyAuthor) => {
    setLoading(true);
    try {
      const { data: res } = await axios.get(
        `${API}/api/admin/synthesis-verification-history?limit=200${by ? `&by=${encodeURIComponent(by)}` : ""}`,
        { withCredentials: true }
      );
      // B1123 — La liste des auteurs vient du serveur : filtrée, la réponse
      // ne contient plus qu'un auteur, ce qui viderait le menu du filtre.
      setData((prev) => ({ ...res, authors: res.authors?.length ? res.authors : prev?.authors || [] }));
    } catch (e) {
      toast.error(tr("Erreur : ", "Error: ") + (e?.response?.data?.detail || e.message));
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [historyAuthor]);

  useEffect(() => { load(); }, [load]);

  const unlock = async (lock) => {
    setRemoving(lock.id);
    try {
      await axios.delete(
        `${API}/api/videos/${lock.video_id}/synthesis/locked-passages/${lock.id}`,
        { withCredentials: true }
      );
      toast.success(tr("Verrou levé.", "Lock released."));
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Suppression impossible");
    } finally {
      setRemoving(null);
    }
  };

  const stale = data?.stale || {};
  // B1112 — En mode « nom de fichier », on trie par fichier pour une revue
  // pas-à-pas ; sinon on garde l'ordre du serveur (les plus périmées d'abord).
  const staleRows = displayBy === "filename"
    ? [...(stale.stale || [])].sort((a, b) =>
        (a.filename || a.title || "").localeCompare(b.filename || b.title || "", "fr",
          { sensitivity: "base", numeric: true }))
    : (stale.stale || []);

  return (
    <div className="max-w-6xl mx-auto p-6 space-y-6" data-testid="synthesis-verifications-page">
      <header className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-zinc-900 flex items-center gap-2">
            <ClockCounterClockwise size={26} className="text-[#002FA7]" />
            {tr("Vérifications des synthèses", "Summary verifications")}
          </h1>
          <p className="text-sm text-zinc-600 mt-1 max-w-2xl">
            {tr(
              "Chaque vérification factuelle et chaque correction appliquée, avec son auteur et ce qui a été trouvé. En dessous : les synthèses modifiées après leur vérification — elles affichent « périmée » dans la vidéo, mais personne n'ouvre les vidéos une par une.",
              "Every fact check and every applied correction, with who did it and what was found. Below: the summaries edited after their check — they show “stale” on the video, but nobody opens videos one by one.",
            )}
          </p>
        </div>
        <button
          onClick={load}
          disabled={loading}
          className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-zinc-300 text-sm text-zinc-700 hover:bg-zinc-50 disabled:opacity-50"
          data-testid="synthesis-verifications-refresh"
        >
          <ArrowClockwise size={16} />
          {tr("Rafraîchir", "Refresh")}
        </button>
      </header>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="synthesis-verifications-stats">
        {[
          { k: "Vérifications", v: data?.verifications ?? "—" },
          { k: "Corrections appliquées", v: data?.corrections ?? "—" },
          { k: tr("Synthèses périmées", "Stale summaries"), v: stale.stale_count ?? "—", danger: (stale.stale_count || 0) >= (stale.threshold || 10) },
          { k: "Jamais vérifiées", v: stale.never_verified_count ?? "—" },
        ].map((c) => (
          <div key={c.k} className="rounded-xl border border-zinc-200 bg-white p-4">
            <div className="text-xs uppercase tracking-wide text-zinc-500">{c.k}</div>
            <div className={`text-2xl font-bold mt-1 ${c.danger ? "text-red-700" : "text-zinc-900"}`}>
              {c.v}
            </div>
          </div>
        ))}
      </div>

      {/* B1123 v2 (Eric 2026-09-08) — L'historique était une SECTION en bas
          de page : Eric ne l'a jamais vu, il cherchait un onglet. Les trois
          blocs deviennent donc trois onglets explicites. */}
      <div className="flex flex-wrap gap-1 border-b border-zinc-200" data-testid="synthesis-verifications-tabs">
        {[
          { id: "perimees", label: `Synthèses périmées (${stale.stale_count || 0})` },
          { id: "verrous", label: `Verrous (${(stale.locks || []).length})` },
          { id: "historique", label: `Historique (${data?.count || 0})` },
        ].map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${
              tab === t.id
                ? "border-[#002FA7] text-[#002FA7]"
                : "border-transparent text-zinc-500 hover:text-zinc-800"
            }`}
            data-testid={`synthesis-tab-${t.id}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === "perimees" && (
      <section className="rounded-xl border border-zinc-200 bg-white p-5" data-testid="stale-syntheses-panel">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h2 className="text-base font-semibold text-zinc-900 flex items-center gap-2">
              <WarningCircle size={18} className="text-amber-600" />
              {tr("Synthèses à re-vérifier", "Summaries to check again")}
            </h2>
            <p className="text-xs text-zinc-500 mt-0.5 mb-3 max-w-2xl">
              {tr("Modifiées après leur dernière vérification factuelle. Un incident s'ouvre au-delà de ", "Edited after their last fact check. An incident opens beyond ")}
              {stale.threshold ?? 10}
              {tr(" synthèses, et la liste part dans le rapport du lundi.", " summaries, and the list goes out in the Monday report.")}
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {/* B1112 (Eric 2026-09-08) — Révision pas-à-pas : Eric travaille
                par fichier, pas par titre de synthèse (deux synthèses peuvent
                porter le même titre). Le tri suit le mode choisi. */}
            <label className="text-xs text-zinc-600 flex items-center gap-1.5">
              {tr("Afficher par :", "Show by:")}
              <select
                value={displayBy}
                onChange={(e) => setDisplayBy(e.target.value)}
                className="rounded-lg border border-zinc-300 px-2 py-1 text-xs"
                data-testid="stale-display-by-select"
              >
                <option value="filename">{tr("Nom de fichier", "File name")}</option>
                <option value="title">{tr("Titre de synthèse", "Summary title")}</option>
              </select>
            </label>
            {/* B1120 (Eric 2026-09-08) — Révision guidée : vider la liste sans
                revenir au tableau entre chaque ligne. */}
            <GuidedStaleReview rows={stale.stale || []} onDone={load} />
            {/* B1125 (Eric 2026-09-08) — Le rappel part le lundi 8 h. Sans
                envoi manuel, il faudrait attendre une semaine pour savoir
                s'il fonctionne. */}
            <button
              onClick={sendReminder}
              disabled={sendingReminder}
              className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-zinc-300 text-zinc-700 text-sm hover:bg-zinc-50 disabled:opacity-50"
              data-testid="send-stale-reminder-btn"
              title={tr("Envoie tout de suite le rappel hebdomadaire aux destinataires de la Sentinelle (n'attend pas lundi 8 h)", "Sends the weekly reminder to the Sentinel recipients right now (does not wait for Monday 8 a.m.)")}
            >
              <EnvelopeSimple size={16} />
              {sendingReminder ? "Envoi…" : "Envoyer le rappel maintenant"}
            </button>
            {/* B1069 — Lot plafonné à 25, avec confirmation chiffrée : chaque
                vérification est un appel payant au fournisseur. */}
            <ReverifyStaleButton staleCount={stale.stale_count || 0} onDone={load} />
          </div>
        </div>
        {(stale.stale || []).length === 0 ? (
          <p className="text-sm text-emerald-800 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2"
             data-testid="stale-syntheses-empty">
            {tr("Aucune synthèse modifiée depuis sa dernière vérification.", "No summary edited since its last check.")}
          </p>
        ) : (
          <table className="w-full text-sm" data-testid="stale-syntheses-table">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wide text-zinc-500 border-b border-zinc-200">
                <th className="py-2 pr-3" data-testid="stale-syntheses-first-col">
                  {displayBy === "filename" ? tr("Nom de fichier", "File name") : tr("Titre de synthèse", "Summary title")}
                </th>
                <th className="py-2 pr-3">{tr("Vérifiée le", "Checked on")}</th>
                <th className="py-2 pr-3">{tr("Modifiée le", "Edited on")}</th>
                <th className="py-2">{tr("Vidéo", "Video")}</th>
              </tr>
            </thead>
            <tbody>
              {staleRows.map((s) => (
                <tr key={s.synthesis_id} className="border-b border-zinc-100"
                    data-testid={`stale-synthesis-${s.synthesis_id}`}>
                  <td className="py-2 pr-3 text-zinc-800" data-testid={`stale-synthesis-label-${s.synthesis_id}`}>
                    {displayBy === "filename" ? (s.filename || s.title) : s.title}
                    <span className="block text-[11px] text-zinc-400">
                      {displayBy === "filename" ? s.title : (s.filename || "")}
                    </span>
                  </td>
                  <td className="py-2 pr-3 text-zinc-600 whitespace-nowrap">{fmt(s.verified_at)}</td>
                  <td className="py-2 pr-3 text-amber-700 whitespace-nowrap">{fmt(s.changed_at)}</td>
                  <td className="py-2 whitespace-nowrap">
                    <Link to={`/dashboard/videos/${s.video_id}`} className="text-[#002FA7] underline">
                      ouvrir
                    </Link>
                    <span className="text-zinc-300 mx-2">|</span>
                    <ReverifyStaleButton
                      oneId={s.synthesis_id}
                      onDone={load}
                      label="re-vérifier"
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>
      )}

      {/* B1074 (Eric 2026-09-04 SPEC STRICK PROD) — Gestion des verrous depuis
          l'admin : sans cette vue, il fallait ouvrir chaque vidéo pour savoir
          ce qui est verrouillé. B1073 — badge « récidiviste » dès 2
          réapplications : c'est le transcript qui contredit la correction. */}
      {tab === "verrous" && (
      <section className="rounded-xl border border-zinc-200 bg-white p-5" data-testid="admin-locks-panel">
        <h2 className="text-base font-semibold text-zinc-900 flex items-center gap-2">
          <Lock size={18} className="text-[#002FA7]" />
          Passages verrouillés ({(data?.locks || []).length})
          {data?.locks_repeat ? (
            <span className="px-1.5 py-0.5 rounded bg-amber-100 text-amber-900 text-[10px] font-semibold"
                  data-testid="admin-locks-repeat-count">
              {data.locks_repeat} récidiviste(s)
            </span>
          ) : null}
        </h2>
        <p className="text-xs text-zinc-500 mt-0.5 mb-3 max-w-3xl">
          {tr("Réappliqués automatiquement après chaque régénération. Un verrou réappliqué ", "Reapplied automatically after every regeneration. A lock reapplied ")}
          {data?.repeat_threshold ?? 2}
          {tr(" fois ou plus est marqué « récidiviste » : ce n'est pas la correction qui pose problème, c'est le transcript qui la contredit.", " times or more is flagged “repeat offender”: the problem is not the correction, it is the transcript contradicting it.")}
        </p>
        {!(data?.locks || []).length ? (
          <p className="text-sm text-zinc-600 bg-zinc-50 border border-zinc-200 rounded-lg px-3 py-2"
             data-testid="admin-locks-empty">
            {tr(
              "Aucun passage verrouillé. Utilise « Verrouiller » sur une correction de l'historique ci-dessous pour empêcher qu'elle soit écrasée.",
              "No locked passage. Use “Lock” on a correction from the history below to stop it being overwritten.",
            )}
          </p>
        ) : (
          <table className="w-full text-sm" data-testid="admin-locks-table">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wide text-zinc-500 border-b border-zinc-200">
                <th className="py-2 pr-3">{tr("Vidéo", "Video")}</th>
                <th className="py-2 pr-3">{tr("Passage verrouillé", "Locked passage")}</th>
                <th className="py-2 pr-3">{tr("Posé par", "Set by")}</th>
                <th className="py-2 pr-3 text-center">{tr("Réappliqué", "Reapplied")}</th>
                <th className="py-2"></th>
              </tr>
            </thead>
            <tbody>
              {data.locks.map((l) => (
                <tr key={l.id} className="border-b border-zinc-100 align-top"
                    data-testid={`admin-lock-${l.id}`}>
                  <td className="py-2 pr-3">
                    <Link to={`/dashboard/videos/${l.video_id}`} className="text-[#002FA7] hover:underline">
                      {l.video_title || l.video_id?.slice(0, 8)}
                    </Link>
                  </td>
                  <td className="py-2 pr-3 text-zinc-800 max-w-md">
                    « {l.locked_text} »
                    {l.repeat && (
                      <span className="ml-2 px-1.5 py-0.5 rounded bg-amber-100 text-amber-900 text-[10px] font-semibold"
                            data-testid={`admin-lock-repeat-${l.id}`}
                            title={tr("Le transcript contredit la correction : corriger la source pour arrêter la répétition.", "The transcript contradicts the correction: fix the source to stop the repetition.")}>
                        {tr("récidiviste", "repeat offender")}
                      </span>
                    )}
                  </td>
                  <td className="py-2 pr-3 text-zinc-600">{l.locked_by}</td>
                  <td className="py-2 pr-3 text-center font-mono">{l.reapplied_count || 0}×</td>
                  <td className="py-2">
                    <button
                      onClick={() => unlock(l)}
                      disabled={removing === l.id}
                      className="text-xs text-zinc-500 hover:text-red-700 disabled:opacity-40"
                      data-testid={`admin-unlock-${l.id}`}
                    >
                      {removing === l.id ? "Levée…" : "Lever le verrou"}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>
      )}

      {tab === "historique" && (
      <section className="rounded-xl border border-zinc-200 bg-white p-5" data-testid="synthesis-history-panel">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h2 className="text-base font-semibold text-zinc-900">
            Historique complet
            {(data?.additions || data?.hidings) ? (
              <span className="ml-2 text-xs font-normal text-zinc-500" data-testid="history-counts">
                {data.additions || 0} ajout(s) · {data.hidings || 0} masquage(s)
              </span>
            ) : null}
          </h2>
          {(data?.authors || []).length > 0 && (
            <div className="flex items-center gap-2">
              <label className="text-xs text-zinc-500" htmlFor="history-author">{tr("Par utilisateur", "By user")}</label>
              <select
                id="history-author"
                value={historyAuthor}
                onChange={(ev) => { setHistoryAuthor(ev.target.value); load(ev.target.value); }}
                className="text-sm border border-zinc-300 rounded-lg px-2 py-1.5 bg-white"
                data-testid="history-author-filter"
              >
                <option value="">{tr("Tous les utilisateurs", "All users")}</option>
                {(data.authors || []).map((a) => (
                  <option key={a} value={a}>{a}</option>
                ))}
              </select>
            </div>
          )}
        </div>
        <div className="mt-3 overflow-x-auto max-h-[32rem] overflow-y-auto">
          <table className="w-full text-sm" data-testid="synthesis-verifications-table">
            <thead className="sticky top-0 bg-white">
              <tr className="text-left text-xs uppercase tracking-wide text-zinc-500 border-b border-zinc-200">
                <th className="py-2 pr-3">Quand</th>
                <th className="py-2 pr-3">Type</th>
                <th className="py-2 pr-3">{tr("Vidéo", "Video")}</th>
                <th className="py-2 pr-3">Par</th>
                <th className="py-2 pr-3">Fournisseur</th>
                <th className="py-2">{tr("Résultat", "Result")}</th>
              </tr>
            </thead>
            <tbody>
              {(data?.events || []).map((e) => (
                <tr key={e.id} className="border-b border-zinc-100" data-testid={`verification-row-${e.id}`}>
                  <td className="py-1.5 pr-3 text-zinc-600 whitespace-nowrap">{fmt(e.at)}</td>
                  <td className="py-1.5 pr-3">
                    {/* B1070 — une régression n'est ni une vérification ni
                        une correction : c'est une erreur qui revient.
                        B1120 — une revue manuelle n'est PAS une vérification
                        factuelle : l'afficher comme telle ferait croire qu'un
                        fournisseur a validé le contenu. */}
                    <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ${
                      e.event === "correction"
                        ? "bg-emerald-100 text-emerald-800"
                        : e.event === "regression"
                          ? "bg-amber-100 text-amber-900"
                          : e.event === "revision_added"
                            ? "bg-sky-100 text-sky-900"
                            : (e.event === "manual_review" || String(e.event || "").startsWith("revision_"))
                              ? "bg-zinc-200 text-zinc-800"
                              : "bg-[#002FA7]/10 text-[#002FA7]"
                    }`}>
                      {e.event === "correction"
                        ? "correction"
                        : e.event === "regression"
                          ? tr("régression", "regression")
                          : e.event === "revision_added"
                            ? "ajout"
                            : e.event === "revision_hidden"
                              ? "masquage"
                              : e.event === "revision_restored"
                                ? tr("réaffichage", "reappearance")
                                : e.event === "manual_review"
                                  ? "revue manuelle"
                                  : "vérification"}
                    </span>
                  </td>
                  <td className="py-1.5 pr-3 text-zinc-700">
                    <Link to={`/dashboard/videos/${e.video_id}`} className="hover:text-[#002FA7]">
                      {e.video_title || e.video_id?.slice(0, 8)}
                    </Link>
                  </td>
                  <td className="py-1.5 pr-3 text-zinc-600">{e.by}</td>
                  <td className="py-1.5 pr-3 font-mono text-xs text-zinc-500">{e.provider || "—"}</td>
                  <td className="py-1.5 text-xs">
                    {/* B1074 — Verrouiller directement depuis l'historique :
                        c'est là qu'on voit quelles corrections méritent de
                        survivre à la prochaine régénération. */}
                    {e.event === "correction" && e.issues?.[0]?.suggested_correction && (
                      <span className="float-right ml-2">
                        <LockPassageButton
                          videoId={e.video_id}
                          lockedText={e.issues[0].suggested_correction}
                          originalText={e.issues[0].original_text || ""}
                          note={e.issues[0].issue || ""}
                          onLocked={load}
                          testId={`admin-lock-btn-${e.id}`}
                          compact
                        />
                      </span>
                    )}
                    {e.event === "correction"
                      ? `${e.applied_count ?? e.claims_count} correction(s) appliquée(s)`
                      : e.event === "regression"
                        ? <span className="text-amber-800">{e.claims_count} erreur(s) réapparue(s)</span>
                        : e.event === "manual_review"
                        ? <span className="text-zinc-700">
                            Relue sans appel IA{e.note ? ` — ${e.note}` : ""}
                          </span>
                        : String(e.event || "").startsWith("revision_")
                        ? <span className="text-zinc-700" title={e.excerpt || ""}>
                            {e.excerpt ? `« ${e.excerpt.slice(0, 70)} »` : "1 révision"}
                          </span>
                        : e.claims_count
                        ? <span className="text-red-700">{e.claims_count} erreur(s) trouvée(s)</span>
                        : <span className="text-emerald-700">{tr("aucune erreur", "no error")}</span>}
                  </td>
                </tr>
              ))}
              {!(data?.events || []).length && (
                <tr>
                  <td colSpan={6} className="py-6 text-center text-zinc-500"
                      data-testid="synthesis-verifications-empty">
                    {loading ? "Chargement…" : "Aucune vérification enregistrée pour l'instant."}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>
      )}
    </div>
  );
}
